import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";

import { Button, Input, Snackbar } from "@chef/components";
import { AppShell } from "@chef/feature-app-shell";
import { useAnonymouslyUnsubscribeFromSmsMutation } from "@chef/state-management";
import { useExtendedForm } from "@chef/hooks";
import { BRAND_PATHS, config } from "@chef/constants";

import { intl } from "./SmsUnsubscribePage.Intl";

type UnsubscribeFormData = yup.InferType<typeof schema>;

const isValidPhoneNumberRegex = new RegExp(
  `[0-9]{${config.phoneNumber.length}}`,
);

const schema = yup.object({
  phone: yup
    .string()
    .matches(isValidPhoneNumberRegex, intl.INVALID_PHONE_NUMBER)
    .required(intl.PHONE_NUMBER_REQUIRED),
});

export const SmsUnsubscribePage = () => {
  const { register, handleSubmit } = useExtendedForm<UnsubscribeFormData>({
    resolver: yupResolver(schema),
    mode: "all",
  });

  const [unsubscribe, { isSuccess, isLoading }] =
    useAnonymouslyUnsubscribeFromSmsMutation();

  const onSubmit = async (data: UnsubscribeFormData) => {
    await unsubscribe({ phone: data.phone }).unwrap();
  };

  return (
    <AppShell title={intl.UNSUBSCRIBE_FROM_SMS} index={false}>
      <AppShell.Header />
      <AppShell.Meta />
      <AppShell.Main grid>
        <div className="flex flex-col gap-6 col-span-full lg:col-span-6">
          <h1>
            <strong>{intl.NO_LONGER_INTERESTED}</strong>
          </h1>
          {!isSuccess ? (
            <>
              <p>{intl.TYPE_IN_YOUR_PHONE_NUMBER}</p>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="grid grid-cols-1 gap-4 md:grid-cols-3"
              >
                <Input
                  {...register("phone")}
                  type="tel"
                  id="phone-input"
                  label={intl.PHONE_NUMBER}
                  helperText={intl.EXAMPLE}
                  className="md:col-span-2"
                  maxLength={config.phoneNumber.length}
                />
                <Button type="submit" loading={isLoading} primary>
                  {intl.CONFIRM_UNSUBSCRIPTION}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Snackbar showIcon={false} success>
                <p>{intl.YOU_ARE_NOW_UNSUBSCRIBED}</p>
              </Snackbar>
              <p className="relative p-4 text-sm rounded bg-grey-3">
                {intl.YOU_WILL_STILL_RECEIVE_SMS_ABOUT_DELIVERIES}
                <Link
                  href={BRAND_PATHS.CONTACT_SETTINGS_PAGE}
                  className="underline"
                >
                  {intl.LOG_IN_HERE}
                </Link>
              </p>
            </>
          )}
          <p className="text-sm">
            {intl.DO_YOU_HAVE_QUESTIONS}{" "}
            <Link
              href={BRAND_PATHS.CONTACT_US_PAGE_LOGOUT}
              className="underline"
            >
              {intl.CONTACT_CUSTOMER_SERVICE_HERE}
            </Link>
          </p>
        </div>
      </AppShell.Main>

      <AppShell.Footer />
      <AppShell.TabBar />
    </AppShell>
  );
};
